import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import { RenderSecure } from "../../util/script/RenderSecure";
import { TableSkeleton } from "../../Components/Accounting/SkeletonLoader";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const ACCOUNT_TYPES = ["asset", "liability", "equity", "income", "expense", "contra_asset", "contra_income", "contra_expense"];

const ChartOfAccounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showSeedModal, setShowSeedModal] = useState(false);
  const [seedIndustry, setSeedIndustry] = useState("generic");
  const [form, setForm] = useState({ account_code: "", name: "", type: "", parent_account_id: "", description: "", allow_direct_posting: 1 });
  const [editModal, setEditModal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAccounts(); }, [filterType]);

  const fetchAccounts = async () => {
    setLoading(true);
    const res = await ajaxAccounting.listAccounts({ type: filterType || undefined });
    if (res.status === "OK") setAccounts(res.details || []);
    else toast.error(res.message || "Failed to fetch accounts");
    setLoading(false);
  };

  const handleSeed = async () => {
    const res = await ajaxAccounting.seedAccounts({ industry: seedIndustry });
    if (res.status === "OK") { toast.success("Chart of Accounts seeded!"); fetchAccounts(); setShowSeedModal(false); }
    else toast.error(res.message);
  };

  const handleCreate = async () => {
    if (!form.account_code || !form.name || !form.type) { toast.error("Account code, name, and type are required."); return; }
    setSaving(true);
    const res = await ajaxAccounting.createAccount({
      account_code: form.account_code, name: form.name, type: form.type,
      parent_account_id: form.parent_account_id ? parseInt(form.parent_account_id) : undefined,
      description: form.description,
      allow_direct_posting: parseInt(form.allow_direct_posting),
    });
    if (res.status === "OK") { toast.success("Account created!"); fetchAccounts(); setShowModal(false); setForm({ account_code: "", name: "", type: "", parent_account_id: "", description: "", allow_direct_posting: 1 }); }
    else toast.error(res.message);
    setSaving(false);
  };

  const openEdit = (row) => setEditModal({ id: row.id, account_code: row.account_code, type: row.type, name: row.name || "", description: row.description || "" });

  const handleUpdate = async () => {
    if (!editModal.name) { toast.error("Account name is required."); return; }
    setSaving(true);
    const res = await ajaxAccounting.updateAccount({ id: editModal.id, name: editModal.name, description: editModal.description });
    if (res.status === "OK") { toast.success("Account updated!"); fetchAccounts(); setEditModal(null); }
    else toast.error(res.message);
    setSaving(false);
  };

  const typeBadge = (type) => {
    const colors = { asset: "primary", liability: "danger", equity: "warning", income: "success", expense: "secondary", contra_asset: "info", contra_income: "info", contra_expense: "info" };
    return <span className={`badge bg-${colors[type] || "light"} text-capitalize`}>{(type || "").replace("_", " ")}</span>;
  };

  const columns = [
    { key: "account_code", label: "Code", render: (v) => <code className="fw-bold">{v}</code> },
    { key: "name", label: "Account Name", render: (v, row) => <span>{(row.parent_id || row.parent_account_id) ? <span style={{ marginLeft: "16px" }}>↳ </span> : ""}{v}</span> },
    { key: "type", label: "Type", render: (v) => typeBadge(v) },
    { key: "allow_direct_posting", label: "Postable", render: (v) => v ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span> },
    { key: "is_active", label: "Status", render: (v) => v !== 0 ? <span className="badge bg-success">Active</span> : <span className="badge bg-danger">Inactive</span> },
    {
      key: "id", label: "Actions",
      render: (id, row) => (
        <div className="d-flex gap-1">
          <button className="btn btn-sm btn-light" title="View Ledger" onClick={() => navigate(`/accounting/ledger?account_id=${id}`)}><i className="fa-solid fa-list"></i></button>
          <button className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => openEdit(row)}><i className="fa-solid fa-pen"></i></button>
        </div>
      ),
    },
  ];

  return (
    <AppContainer
      title="Chart of Accounts"
      previousPageTitle="Accounting"
      description="The chart of accounts is your master list of all financial accounts. Accounts are organized by type: Assets, Liabilities, Equity, Revenue, and Expenses. Use 'Seed' to auto-generate a standard chart for your industry."
      actions={[
        { label: "New Account", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => setShowModal(true) },
        { label: "Seed Accounts", icon: "fa-solid fa-wand-magic-sparkles", className: "btn btn-outline-secondary", onClick: () => setShowSeedModal(true) },
      ]}
    >
      <Toaster />

      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Filter by Type</label>
            <Select2 options={[{ value: "", label: "All Types" }, ...ACCOUNT_TYPES.map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))]} value={filterType} onChange={setFilterType} placeholder="All Types" />
          </div>
          <div className="col-lg-8 d-flex align-items-end gap-2">
            {ACCOUNT_TYPES.map((t) => {
              const colors = { asset: "#6366f1", liability: "#ef4444", equity: "#f59e0b", income: "#10b981", expense: "#8b5cf6", contra_asset: "#0ea5e9", contra_income: "#0ea5e9", contra_expense: "#0ea5e9" };
              const count = accounts.filter((a) => a.type === t).length;
              return (
                <div key={t} className="card border-0 text-center p-2" style={{ minWidth: "90px", borderLeft: `3px solid ${colors[t]}`, flex: 1 }}>
                  <div className="fw-bold" style={{ color: colors[t] }}>{count}</div>
                  <div className="tx-11 tx-muted text-capitalize">{t}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card custom-card p-3">
        {loading ? <TableSkeleton rows={8} cols={6} /> : (
          <DataTable columns={columns} data={accounts} loading={false} pageSize={20} emptyMessage="No accounts found. Use 'Seed Accounts' to auto-generate a standard chart." />
        )}
      </div>

      {/* Create Account Modal */}
      {showModal && (
        <AppModal
          title="Create Account"
          size="lg"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? "Saving..." : "Create Account"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Account Code <span className="tx-danger">*</span></label>
              <input className="form-control" value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })} placeholder="e.g. 1010" />
            </div>
            <div className="col-lg-8">
              <label className="form-label tx-12 fw-semibold">Account Name <span className="tx-danger">*</span></label>
              <input className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Cash in Hand" />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Account Type <span className="tx-danger">*</span></label>
              <Select2 options={ACCOUNT_TYPES.map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))} value={form.type} onChange={(v) => setForm({ ...form, type: v })} placeholder="Select Type" />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Parent Account</label>
              <Select2 options={accounts.map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }))} value={form.parent_account_id} onChange={(v) => setForm({ ...form, parent_account_id: v })} placeholder="No parent (top-level)" />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Description</label>
              <textarea className="form-control" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Allow Direct Posting</label>
              <Select2 options={[{ value: "1", label: "Yes" }, { value: "0", label: "No (header only)" }]} value={String(form.allow_direct_posting)} onChange={(v) => setForm({ ...form, allow_direct_posting: parseInt(v) })} />
            </div>
          </div>
        </AppModal>
      )}

      {/* Seed Modal */}
      {showSeedModal && (
        <AppModal
          title="Seed Chart of Accounts"
          onClose={() => setShowSeedModal(false)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setShowSeedModal(false)}>Cancel</button>
              <button className="btn btn-warning" onClick={handleSeed}><i className="fa-solid fa-wand-magic-sparkles me-1"></i>Seed Now</button>
            </>
          }
        >
          <div className="alert alert-warning tx-13">This will create standard accounts for your industry. Existing accounts will not be overwritten.</div>
          <label className="form-label tx-12 fw-semibold">Industry</label>
          <Select2 options={[{ value: "generic", label: "Generic" }, { value: "services", label: "Services" }, { value: "trading", label: "Trading" }, { value: "manufacturing", label: "Manufacturing" }, { value: "school", label: "School / Education" }]} value={seedIndustry} onChange={setSeedIndustry} />
        </AppModal>
      )}
      {/* Edit Account Modal */}
      {editModal && (
        <AppModal
          title={<>Edit Account — <code>{editModal.account_code}</code></>}
          size="lg"
          onClose={() => setEditModal(null)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
            </>
          }
        >
          <div className="alert alert-light tx-12 mb-3">Account code and type are structural and cannot be changed here.</div>
          <div className="row g-3">
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Account Code</label>
              <input className="form-control" value={editModal.account_code} disabled readOnly />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Type</label>
              <input className="form-control text-capitalize" value={editModal.type} disabled readOnly />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Account Name <span className="tx-danger">*</span></label>
              <input className="form-control" value={editModal.name} onChange={(e) => setEditModal({ ...editModal, name: e.target.value })} />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Description</label>
              <textarea className="form-control" rows={2} value={editModal.description} onChange={(e) => setEditModal({ ...editModal, description: e.target.value })} />
            </div>
          </div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default ChartOfAccounts;
